import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { MapPin, Mail, Send, Loader2, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/Button';
import { EMAIL_CONFIG, isEmailConfigured } from '../../services/emailService';
import styles from './Contact.module.css';

interface FormData {
  name: string;
  email: string;
  company: string;
  phone: string;
  subject: string;
  message: string;
  website: string; // Honeypot field
  consent: boolean;
}

export const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: 'consultation',
    message: '',
    website: '', // Honeypot field
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');



  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    setFormData((prev) => ({ ...prev, [target.name]: value }));
  };

  const getSubjectLabel = (subjectKey: string): string => {
    const subjects = (t.contact.form.subjects || {}) as Record<string, string>;
    return subjects[subjectKey] || subjectKey;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot spam check - if this field is filled, it's a bot
    if (formData.website) {
      console.log('Bot detected via honeypot');
      // Fake success to not alert the bot
      setSubmitStatus('success');
      setTimeout(() => {
        setSubmitStatus('idle');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: 'consultation',
          message: '',
          website: '',
          consent: false,
        });
      }, 3000);
      return;
    }

    if (!formData.consent) {
      setSubmitStatus('error');
      setErrorMessage(t.contact.form.consentRequired || 'Please confirm consent.');
      return;
    }



    setIsSubmitting(true);
    setErrorMessage('');

    // Check if EmailJS is configured
    if (!isEmailConfigured()) {
      console.warn('EmailJS not configured. Form data:', formData);
      setSubmitStatus('error');
      setErrorMessage(t.contact.form.submitError || 'Submission failed.');
      setIsSubmitting(false);
      return;
    }

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      company: formData.company || 'Not provided',
      phone: formData.phone || 'Not provided',
      subject: getSubjectLabel(formData.subject),
      message: formData.message,
      consent: formData.consent ? 'yes' : 'no',
      consent_timestamp: new Date().toISOString(),
      to_email: 'info@integrovaai.de',
    };

    try {
      await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        EMAIL_CONFIG.TEMPLATE_ID_CONTACT,
        templateParams,
        EMAIL_CONFIG.PUBLIC_KEY
      );
      setSubmitStatus('success');
      setErrorMessage('');
      // Reset form after success
      setTimeout(() => {
        setSubmitStatus('idle');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: 'consultation',
          message: '',
          website: '',
          consent: false,
        });
      }, 3000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
      setErrorMessage(t.contact.form.submitError || 'Submission failed.');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonContent = () => {
    if (isSubmitting) {
      return (
        <>
          <Loader2 size={18} className={styles.spinner} />
          {t.contact.form.submit}...
        </>
      );
    }
    if (submitStatus === 'success') {
      return (
        <>
          <CheckCircle2 size={18} />
          {t.contact.form.submit} ✓
        </>
      );
    }
    if (submitStatus === 'error') {
      return (
        <>
          <AlertCircle size={18} />
          {t.contact.form.submitErrorButton}
        </>
      );
    }
    return (
      <>
        <Send size={18} />
        {t.contact.form.submit}
      </>
    );
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/integrovaai-info/30min', '_blank', 'noopener,noreferrer');
  };

  return (
    <section className={styles.contact} id="contact">
      <div className={styles.contact__background}>
        <div className={styles.contact__glow1} />
        <div className={styles.contact__glow2} />
      </div>

      <div className={styles.contact__container}>
        {/* Header */}
        <motion.div
          className={styles.contact__header}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.15 }}
        >
          <span className={styles.contact__tag}><Sparkles size={14} />{t.contact.tag}</span>
          <h2 className={styles.contact__title}>{t.contact.title}</h2>
          <p className={styles.contact__subtitle}>{t.contact.subtitle}</p>
          {t.contact.helper && (
            <p className={styles.contact__helper}>{t.contact.helper}</p>
          )}
        </motion.div>

        <div className={styles.contact__grid}>
          {/* Contact Info */}
          <motion.div
            className={styles.contact__info}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.15 }}
          >
            <div className={styles.contact__infoCard}>
              <h3 className={styles.contact__infoTitle}>Integrova AI GmbH</h3>

              <div className={styles.contact__infoItems}>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(t.contact.info.address)}`}
                  className={styles.contact__infoItem}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={styles.contact__infoItemIcon}>
                    <MapPin size={20} />
                  </div>
                  <span>{t.contact.info.address}</span>
                </a>

                <a href={`mailto:${t.contact.info.email}`} className={styles.contact__infoItem}>
                  <div className={styles.contact__infoItemIcon}>
                    <Mail size={20} />
                  </div>
                  <span>{t.contact.info.email}</span>
                </a>

              </div>

              <div className={styles.contact__infoDivider} />

              <p className={styles.contact__infoText}>
                {t.contact.subtitle}
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            className={styles.contact__form}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.15 }}
          >
            <div className={styles.contact__formRow}>
              <div className={styles.contact__formGroup}>
                <label htmlFor="name" className={styles.contact__formLabel}>
                  {t.contact.form.name} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.contact__formInput}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.contact__formGroup}>
                <label htmlFor="email" className={styles.contact__formLabel}>
                  {t.contact.form.email} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.contact__formInput}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.contact__formRow}>
              <div className={styles.contact__formGroup}>
                <label htmlFor="company" className={styles.contact__formLabel}>
                  {t.contact.form.company}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={styles.contact__formInput}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.contact__formGroup}>
                <label htmlFor="phone" className={styles.contact__formLabel}>
                  {t.contact.form.phone}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={styles.contact__formInput}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.contact__formGroup}>
              <label htmlFor="subject" className={styles.contact__formLabel}>
                {t.contact.form.subject} *
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={styles.contact__formSelect}
                disabled={isSubmitting}
              >
                <option value="consultation">{t.contact.form.subjects?.consultation || 'Consultation'}</option>
                <option value="project">{t.contact.form.subjects?.project || 'Project'}</option>
                <option value="partnership">{t.contact.form.subjects?.partnership || 'Partnership'}</option>
                <option value="other">{t.contact.form.subjects?.other || 'Other'}</option>
              </select>
            </div>

            <div className={styles.contact__formGroup}>
              <label htmlFor="message" className={styles.contact__formLabel}>
                {t.contact.form.message} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={styles.contact__formTextarea}
                rows={5}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.contact__consent}>
              <label className={styles.contact__consentLabel}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={handleChange}
                  className={styles.contact__consentCheckbox}
                  disabled={isSubmitting}
                  required
                />
                <span>
                  {t.contact.form.consentLabel}{' '}
                  <Link to="/datenschutz" className={styles.contact__consentLink}>
                    {t.contact.form.consentLink}
                  </Link>{' '}
                  {t.contact.form.consentLabelSuffix}
                </span>
              </label>
            </div>

            {/* Honeypot field - hidden from humans, bots fill it */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                type="text"
                id="contact-website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className={styles.contact__action}>
              <div className={styles.contact__trustBadge}>
                <span className={styles.contact__trustDot} />
                <span>{t.contact.form.secureBadge}</span>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={isSubmitting || submitStatus === 'success'}
                className={submitStatus === 'success' ? styles.successButton : submitStatus === 'error' ? styles.errorButton : ''}
              >
                {getButtonContent()}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="lg"
                fullWidth
                onClick={handleBookCall}
              >
                {t.contact.form.calendlyCta}
              </Button>
              {errorMessage && (
                <p className={styles.contact__error}>{errorMessage}</p>
              )}
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
